import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { fileNameEdit } from 'helpers/FileUtils';
import _ from 'lodash';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/gender_age/');

const format = (v) => `${v}%`;

const defaultState = {
  chart1: {
    barChart: [],
    maxValue: 0,
    barChangeLine: [],
  },
  chart2: {
    barChart: [],
    maxValue: 0,
    barChangeLine: [],
  },
  chart3: {
    barChart: [],
    maxValue: 0,
    barChangeLine: [],
  },
  chart4: {
    barChart: [],
    maxValue: 0,
    barChangeLine: [],
  },
};

const useInvoicesTickets = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let table1, table2, table3, table4;

        if (!isAllReports) {
          const {
            data: {
              table1: backTable1,
              table2: backTable2,
              table3: backTable3,
              table4: backTable$,
            },
          } = await api.getFootfalls({ params });
          table1 = backTable1;
          table2 = backTable2;
          table3 = backTable3;
          table4 = backTable$;
        } else {
          const {
            table1: backTable1,
            table2: backTable2,
            table3: backTable3,
            table4: backTable$,
          } = allReportsData;
          table1 = backTable1;
          table2 = backTable2;
          table3 = backTable3;
          table4 = backTable$;
        }

        const maxValueTable1 = Helpers.getMaxValuFromArrayOfObject(
          table1,
          'percent',
        );
        const maxValueTable2Man = Helpers.getMaxValuFromArrayOfObject(
          table2.data,
          'Maschi',
        );

        const maxValueTable2Woman = Helpers.getMaxValuFromArrayOfObject(
          table2.data,
          'Femmine',
        );

        const maxValueTable3 = Helpers.getMaxValuFromArrayOfObject(
          table3,
          'age',
        );

        const maxValueTable4 = Helpers.getMaxValuFromArrayOfObject(
          table4,
          'percent',
        );

        const barChart1 = table1.map((i) => ({
          date: i.title,
          Sesso: i.percent,
          SessoColor: '#61cdbb',
        }));

        const barChangeLine1 = {
          id: 'Spesa media',
          data: table1.map((item) => ({
            x: item['title'],
            y: item['avg_amount'],
            tooltipKeys: [
              {
                key: 'Sesso',
                value: Helpers.changeDotToComa(item.percent),
                color: '#61cdbb',
                percent: '%',
              },
              {
                key: 'Spesa media',
                value: Helpers.changeDotToComa(item['avg_amount']),
                color: '#f47560',
                percent: '%',
              },
            ],
          })),
        };

        const barChart2 = table2.data.map((i) => ({
          date: i.title,
          Maschi: i.Maschi,
          MaschiColor: '#61cdbb',
          Femmine: i.Femmine,
          FemmineColor: '#f47560',
        }));

        const barChart3 = table3.map((i) => ({
          date: i.title,
          'Età ': i.age,
          'Età Color': '#61cdbb',
        }));

        const barChangeLine3 = {
          id: 'Spesa media',
          data: table3.map((item) => ({
            x: item['title'],
            y: item['spesa media'],
            tooltipKeys: [
              {
                key: 'Età ',
                value: Helpers.changeDotToComa(item.age),
                color: '#61cdbb',
                percent: '%',
              },
              {
                key: 'Spesa media',
                value: Helpers.changeDotToComa(item['spesa media']),
                color: '#f47560',
                percent: '%',
              },
            ],
          })),
        };

        const barChart4 = table4.map((i) => ({
          date: i.title,
          'Incidenza per provincia': i.percent,
          'Incidenza per provinciaColor': '#61cdbb',
        }));

        const barChangeLine4 = {
          id: 'Spesa media',
          data: table4.map((item) => ({
            x: item['title'],
            y: item['avg_amount'],
            tooltipKeys: [
              {
                key: 'Incidenza per provincia',
                value: Helpers.changeDotToComa(item.percent),
                color: '#61cdbb',
                percent: '%',
              },
              {
                key: 'Spesa media',
                value: Helpers.changeDotToComa(item['avg_amount']),
                color: '#f47560',
                percent: '%',
              },
            ],
          })),
        };

        setData({
          chart1: {
            barChart: barChart1,
            maxValue: maxValueTable1,
            barChangeLine: [barChangeLine1],
            tableData: table1,
          },
          chart2: {
            barChart: barChart2,
            maxValue: _.max([maxValueTable2Man, maxValueTable2Woman]),
            tableData: table2.data,
            barChangeLine: [],
          },
          chart3: {
            barChart: barChart3,
            maxValue: maxValueTable3,
            barChangeLine: [barChangeLine3],
            tableData: table3,
          },
          chart4: {
            barChart: barChart4,
            maxValue: maxValueTable4,
            barChangeLine: [barChangeLine4],
            tableData: table4,
          },
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    data,
    filters,
    format,
    loading,
    handleDownloadExcel,
    title,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesTickets;
